import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { nanoid } from 'nanoid';
import type { SceneJson, Scene } from '@/types/scene';
import type { Project } from '@/types/project';
import { projectsApi } from '@/services/api/projects';

interface EditorState {
  projectId: string | null;
  title: string;
  sceneJson: SceneJson | null;
  selectedSceneId: string | null;
  isDirty: boolean;
  isLoading: boolean;
  renderStatus: 'idle' | 'rendering' | 'completed' | 'failed';
  renderProgress: number;

  loadProject: (id: string) => Promise<void>;
  newProject: () => void;
  setTitle: (title: string) => void;
  addScene: () => void;
  updateScene: (id: string, patch: Partial<Scene>) => void;
  removeScene: (id: string) => void;
  reorderScenes: (from: number, to: number) => void;
  selectScene: (id: string | null) => void;
  save: () => Promise<Project | null>;
  setRenderStatus: (s: EditorState['renderStatus']) => void;
  setRenderProgress: (p: number) => void;
}

function emptySceneJson(): SceneJson {
  return {
    version: '1.0',
    title: '新しいプロジェクト',
    total_duration: 5,
    scenes: [
      {
        id: nanoid(8),
        t_start: 0,
        t_end: 5,
        narration: 'ここにナレーションを入力してください。',
        emotion: 'neutral',
        camera: { preset: 'bust', transition: 'cut' },
        caption: { text: 'タイトル', style: 'yellow_impact', position: 'top' },
        background: { type: 'gradient', value: 'linear-gradient(135deg,#4338ca,#8b5cf6)' },
      },
    ],
  };
}

function recomputeDurations(scenes: Scene[], total: number) {
  // If scene end-times drift, normalize total_duration = max(t_end)
  const maxEnd = scenes.reduce((m, s) => Math.max(m, s.t_end), 0);
  return Math.max(total, maxEnd);
}

export const useEditorStore = create<EditorState>()(
  immer((set, get) => ({
    projectId: null,
    title: '',
    sceneJson: null,
    selectedSceneId: null,
    isDirty: false,
    isLoading: false,
    renderStatus: 'idle',
    renderProgress: 0,

    loadProject: async (id) => {
      set((s) => {
        s.isLoading = true;
      });
      try {
        const project = await projectsApi.get(id);
        set((s) => {
          s.projectId = project.id;
          s.title = project.title;
          s.sceneJson = project.sceneJson;
          s.selectedSceneId = project.sceneJson.scenes[0]?.id ?? null;
          s.isDirty = false;
          s.isLoading = false;
        });
      } catch (e) {
        set((s) => {
          s.isLoading = false;
        });
        throw e;
      }
    },

    newProject: () => {
      const sj = emptySceneJson();
      set((s) => {
        s.projectId = null;
        s.title = sj.title;
        s.sceneJson = sj;
        s.selectedSceneId = sj.scenes[0]?.id ?? null;
        s.isDirty = true;
      });
    },

    setTitle: (title) =>
      set((s) => {
        s.title = title;
        if (s.sceneJson) s.sceneJson.title = title;
        s.isDirty = true;
      }),

    addScene: () =>
      set((s) => {
        if (!s.sceneJson) return;
        const last = s.sceneJson.scenes.at(-1);
        const start = last ? last.t_end : 0;
        const newScene: Scene = {
          id: nanoid(8),
          t_start: start,
          t_end: start + 5,
          narration: '',
          emotion: 'neutral',
          camera: { preset: 'bust', transition: 'cut' },
          caption: null,
          background: { type: 'color', value: '#0f172a' },
        };
        s.sceneJson.scenes.push(newScene);
        s.sceneJson.total_duration = recomputeDurations(
          s.sceneJson.scenes,
          s.sceneJson.total_duration,
        );
        s.selectedSceneId = newScene.id;
        s.isDirty = true;
      }),

    updateScene: (id, patch) =>
      set((s) => {
        if (!s.sceneJson) return;
        const idx = s.sceneJson.scenes.findIndex((x) => x.id === id);
        if (idx === -1) return;
        const scene = s.sceneJson.scenes[idx];
        if (!scene) return;
        Object.assign(scene, patch);
        s.sceneJson.total_duration = recomputeDurations(
          s.sceneJson.scenes,
          s.sceneJson.total_duration,
        );
        s.isDirty = true;
      }),

    removeScene: (id) =>
      set((s) => {
        if (!s.sceneJson) return;
        s.sceneJson.scenes = s.sceneJson.scenes.filter((x) => x.id !== id);
        if (s.selectedSceneId === id) {
          s.selectedSceneId = s.sceneJson.scenes[0]?.id ?? null;
        }
        s.isDirty = true;
      }),

    reorderScenes: (from, to) =>
      set((s) => {
        if (!s.sceneJson) return;
        const arr = s.sceneJson.scenes;
        if (from < 0 || from >= arr.length || to < 0 || to >= arr.length) return;
        const [moved] = arr.splice(from, 1);
        if (moved) arr.splice(to, 0, moved);
        s.isDirty = true;
      }),

    selectScene: (id) =>
      set((s) => {
        s.selectedSceneId = id;
      }),

    save: async () => {
      const { projectId, sceneJson, title } = get();
      if (!sceneJson) return null;
      const payload = { ...sceneJson, title };
      if (projectId) {
        const updated = await projectsApi.update(projectId, {
          title,
          sceneJson: payload,
        });
        set((s) => {
          s.isDirty = false;
        });
        return updated;
      } else {
        const created = await projectsApi.create({ title, sceneJson: payload });
        set((s) => {
          s.projectId = created.id;
          s.isDirty = false;
        });
        return created;
      }
    },

    setRenderStatus: (status) =>
      set((s) => {
        s.renderStatus = status;
      }),

    setRenderProgress: (p) =>
      set((s) => {
        s.renderProgress = p;
      }),
  })),
);
